import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";

import ConnectingScreen from "../ConnectingScreen/ConnectingScreen";
import UploadFiles from "../../UploadFiles/UploadFiles";
import { dualStreamEnabled, singleUserEnabled } from "../../../shared/utility";
import WaitingScreen from "../VideoCallScreen/WaitingScreen/WaitingScreen";
import VideoScreen from "../VideoCallScreen/VideoScreen/VideoScreen";
import { setJoinMeetingSmsTemplate } from "../../../reducers/invite-reducer";
import { setCurrentCallBodyScreen } from "../../../reducers/common-reducer";
import NotifyParticipant from "../../UploadFiles/NotifyParticipant";
import PrepareRecordingView from "../../Recording/PrepareRecordingView";
import { CALL_BODY_SCREEN } from "../../../constants/Constants";
import { PublicVariables } from "../../../constants/PublicVariables";
import LiveLocation from "../../LiveLocation/LiveLocation";
import { isLiveLocationButtonIsVisible } from "../../LiveLocation/LocationUtils";
import GalleryView from "../../GalleryView/GalleryView";
import useCheckCallConnected from "../../../hooks/useCheckCallConnected";
import { attachSinkId } from "../CallTopbar/CallSettings/CallSettingUtils";
import FullHeightLoader from "../../UI/Loader/FullHeightLoader";

const CallBody = ({ handleToShowPrivacyPolicyPopup, changeConnectingScreenStep, localisedValuesForCallBody, dispatchJoinMeeting }) => {
    const { joinMeetingData } = useSelector((state) => state.joinMeeting);

    const dispatch = useDispatch();
    const isCallNotConnected = useCheckCallConnected();
    const { currentSpeakerDevice } = useSelector((state) => state.callSetting);

    const { isRoomConnected, isRemoteParticipantJoined, isCallDisconnected, currentCallBodyScreen, expandView } = useSelector(
        (state) => state.commonReducer
    );
    const { isOfflineModeInProgress } = useSelector((state) => state.offlineMode);
    const { recordInProcess } = useSelector((state) => state.recording);
    /* Handle to show waiting screen after Join api call successfully */
    useEffect(() => {
        if (!joinMeetingData && !isRoomConnected && isCallDisconnected && !isRemoteParticipantJoined) {
            dispatch(setCurrentCallBodyScreen(CALL_BODY_SCREEN.CONNECTING_SCREEN));
        } else if (joinMeetingData && PublicVariables.isOnlyUploadImageMeet) {
            dispatch(setCurrentCallBodyScreen(CALL_BODY_SCREEN.UPLOAD_FILE_SCREEN));
        } else if (joinMeetingData && !isCallDisconnected && !isRemoteParticipantJoined) {
            dispatch(setCurrentCallBodyScreen(CALL_BODY_SCREEN.WAITING_SCREEN));
        } else if (
            (joinMeetingData && isRoomConnected && !isCallDisconnected && isRemoteParticipantJoined) ||
            (joinMeetingData && isRoomConnected && !isCallDisconnected && isRemoteParticipantJoined && singleUserEnabled())
        ) {
            if (!dualStreamEnabled() || expandView) {
                dispatch(setCurrentCallBodyScreen(CALL_BODY_SCREEN.VIDEO_SCREEN));
            } else if (dualStreamEnabled() && !expandView) {
                dispatch(setCurrentCallBodyScreen(CALL_BODY_SCREEN.GALLERY_VIEW));
            }
        }
    }, [joinMeetingData, isCallDisconnected, isRemoteParticipantJoined, isRoomConnected]);

    useEffect(() => {
        if (joinMeetingData?.smsTemplate) {
            dispatch(setJoinMeetingSmsTemplate(joinMeetingData.smsTemplate));
        }
    }, [joinMeetingData]);

    useEffect(() => {
        // setting current selected speaker device to main stream
        if (currentSpeakerDevice?.deviceId) {
            attachSinkId(currentSpeakerDevice?.deviceId, isCallNotConnected); // changeAudioOutput function from old code
        }
    }, [currentSpeakerDevice]);

    return (
        //pending to change conditions
        <>
            {isOfflineModeInProgress ? (
                <FullHeightLoader />
            ) : (
                <div className='call-body' id='call_body'>
                    <div id='audioTag' className='position-absolute'></div>
                    {currentCallBodyScreen === CALL_BODY_SCREEN.CONNECTING_SCREEN && (
                        <ConnectingScreen
                            handleToShowPrivacyPolicyPopup={handleToShowPrivacyPolicyPopup}
                            changeConnectingScreenStep={changeConnectingScreenStep}
                            localisedValuesForCallBody={localisedValuesForCallBody}
                        />
                    )}
                    {currentCallBodyScreen === CALL_BODY_SCREEN.WAITING_SCREEN && (
                        <WaitingScreen localisedValuesForCallBody={localisedValuesForCallBody} />
                    )}
                    {currentCallBodyScreen === CALL_BODY_SCREEN.VIDEO_SCREEN && (
                        <VideoScreen localisedValuesForCallBody={localisedValuesForCallBody} />
                    )}
                    {currentCallBodyScreen === CALL_BODY_SCREEN.GALLERY_VIEW && !expandView && (
                        <GalleryView localisedValuesForCallBody={localisedValuesForCallBody} />
                    )}
                    {isRemoteParticipantJoined && recordInProcess && <PrepareRecordingView />}
                    {currentCallBodyScreen === CALL_BODY_SCREEN.NOTIFY_SCREEN && <NotifyParticipant />}
                    {currentCallBodyScreen === CALL_BODY_SCREEN.UPLOAD_FILE_SCREEN && <UploadFiles dispatchJoinMeeting={dispatchJoinMeeting} />}
                    {isLiveLocationButtonIsVisible() && <LiveLocation />}
                </div>
            )}
        </>
    );
};

CallBody.propTypes = {
    handleToShowPrivacyPolicyPopup: PropTypes.func.isRequired,
    changeConnectingScreenStep: PropTypes.string,
    localisedValuesForCallBody: PropTypes.object.isRequired,
};

export default CallBody;
